import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Select, { components, createFilter } from 'react-select';
import { withTheme } from 'styled-components';
import { useIntl } from 'react-intl';

import { inputSliceActions, selectActiveSelectId } from 'features/input/inputSlice';

import StyledAdvancedSelect, { selectStyles } from './StyledAdvancedSelect';

import { AdvancedSelectProps } from './AdvancedSelectProps';
import { AnimatePresence, motion } from 'framer-motion';

const Menu = (props: any) => {
  return (
    <>
      <AnimatePresence>
        <components.Menu {...props}>
          <div>
            <motion.div initial={{ y: -25, opacity: 0 }} animate={{ y: 0, opacity: 1 }}>
              <div>{props.children}</div>
            </motion.div>
          </div>
        </components.Menu>
      </AnimatePresence>
    </>
  );
};

const AdvancedSelect: React.FC<AdvancedSelectProps> = ({
  title,
  options,
  onChange,
  select,
  theme,
  placeholder,
  disabled,
  isClearable,
}) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const activeSelectId = useSelector(selectActiveSelectId);
  const isSelected = select.id === activeSelectId;

  const { value } = select;

  const selectRef: any = useRef();

  useEffect(() => {
    if (isSelected) {
      const select = selectRef.current;
      select.focus();
    }
  }, [selectRef, isSelected]);

  const chosen = value ? options.find((option) => option.value === value) : null;

  const onFocus = () => {
    dispatch(inputSliceActions.setActiveSelect(select.id));
  };

  const filterOptions: any = {
    stringify: (option: any) => option.label,
  };

  return (
    <StyledAdvancedSelect>
      <div className="label">{title}</div>

      <Select
        styles={selectStyles(theme)}
        classNamePrefix="select"
        isDisabled={disabled}
        autoFocus={isSelected && !chosen}
        openMenuOnFocus={isSelected && !chosen}
        defaultMenuIsOpen={isSelected}
        isSearchable={true}
        isClearable={isClearable === true}
        options={options}
        ref={selectRef}
        value={chosen}
        placeholder={
          placeholder ||
          intl.formatMessage(
            {
              id: 'inputs.choose',
            },
            {
              label: title.toLowerCase(),
            },
          )
        }
        filterOption={createFilter(filterOptions)}
        components={{
          DropdownIndicator: () => null,
          IndicatorSeparator: () => null,
          Menu,
        }}
        onFocus={onFocus}
        noOptionsMessage={() => intl.formatMessage({ id: 'inputs.noResult' })}
        onChange={(option) => {
          dispatch(inputSliceActions.setActiveSelect(select.id));
          if (onChange) onChange(option);
        }}
      />
    </StyledAdvancedSelect>
  );
};

export default withTheme(AdvancedSelect);
